import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { Building2, CheckCircle2, ArrowRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const JoinBroker = () => {
  const { brokerId } = useParams();
  const navigate = useNavigate();
  const { token, agent } = useAuth();
  const [broker, setBroker] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(false);
  const [requested, setRequested] = useState(false);

  useEffect(() => {
    fetchBrokerInfo();
  }, [brokerId]);

  const fetchBrokerInfo = async () => {
    try {
      const response = await axios.get(`${API}/broker/info/${brokerId}`);
      setBroker(response.data);
    } catch (error) {
      toast.error('Broker not found');
    } finally {
      setLoading(false);
    }
  };

  const handleJoinRequest = async () => {
    if (!token) {
      // Fetch onboard path and redirect to registration with brokerId
      try {
        const res = await axios.get(`${API}/public/onboard-path`);
        navigate(`${res.data.path}?brokerId=${brokerId}`);
      } catch {
        toast.error('Registration is currently unavailable. Please contact the broker directly.');
      }
      return;
    }

    if (agent?.role !== 'agent') {
      toast.error('Only agents can join brokers');
      return;
    }

    if (agent?.broker_id) {
      toast.error('You are already part of a broker');
      return;
    }

    setRequesting(true);
    try {
      await axios.post(
        `${API}/join-requests`,
        { broker_id: brokerId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRequested(true);
      toast.success('Join request sent! Waiting for broker approval');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send request');
    } finally {
      setRequesting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-stone-500">Loading...</p>
      </div>
    );
  }

  if (!broker) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-stone-600">Broker not found</p>
      </div>
    );
  }

  if (requested) {
    return (
      <div className="min-h-screen relative flex items-center justify-center p-4">
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1762117360871-f11fbad00ee1?crop=entropy&cs=srgb&fm=jpg&q=85)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'brightness(0.5)'
          }}
        />
        
        <div className="glass-card rounded-2xl p-8 md:p-12 w-full max-w-md z-10 text-center">
          <CheckCircle2 className="mx-auto mb-6 text-green-600" size={64} />
          <h1 className="text-3xl md:text-4xl font-bold text-[#1A4D2E] mb-3" style={{fontFamily: '"Playfair Display", serif'}}>
            Request Sent!
          </h1>
          <p className="text-lg text-stone-600 mb-6">
            Your request to join {broker.name} has been sent. You'll be notified once approved.
          </p>
          <Button
            onClick={() => navigate('/dashboard')}
            className="bg-[#1A4D2E] text-white rounded-full px-8 py-6 hover:bg-[#143D24]"
          >
            Go to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative flex items-center justify-center p-4">
      <div 
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1762117360871-f11fbad00ee1?crop=entropy&cs=srgb&fm=jpg&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'brightness(0.5)'
        }}
      />
      
      <div className="glass-card rounded-2xl p-8 md:p-12 w-full max-w-md z-10">
        <div className="text-center mb-8">
          <div className="inline-block p-4 rounded-full bg-[#1A4D2E]/10 mb-4">
            <Building2 className="text-[#1A4D2E]" size={48} />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-[#1A4D2E] mb-3" style={{fontFamily: '"Playfair Display", serif'}}>
            Join Broker
          </h1>
          <Card className="bg-white p-6 rounded-xl mb-6">
            <h2 className="text-2xl font-bold text-[#1A4D2E] mb-2" style={{fontFamily: '"Playfair Display", serif'}}>
              {broker.name}
            </h2>
            <p className="text-stone-600">{broker.email}</p>
          </Card>
          <p className="text-stone-600 mb-6">
            Send a request to join this brokerage. The broker will review and approve your request.
          </p>
        </div>

        <Button
          data-testid="send-join-request"
          onClick={handleJoinRequest}
          disabled={requesting}
          className="w-full bg-[#1A4D2E] text-white rounded-full h-14 text-lg hover:bg-[#143D24] transition-transform hover:scale-105 active:scale-95"
        >
          {requesting ? 'Sending Request...' : 'Send Join Request'}
          <ArrowRight className="ml-2" size={20} />
        </Button>

        {!token && (
          <p className="text-center mt-4 text-sm text-stone-600">
            Don't have an account?{' '}
            <button
              onClick={async () => {
                try {
                  const res = await axios.get(`${API}/public/onboard-path`);
                  navigate(`${res.data.path}?brokerId=${brokerId}`);
                } catch {
                  toast.error('Registration is currently unavailable. Please contact the broker directly.');
                }
              }}
              className="text-[#1A4D2E] font-semibold hover:underline"
            >
              Register as Agent
            </button>
          </p>
        )}
      </div>
    </div>
  );
};

export default JoinBroker;
